// Screen lock: after a stretch of inactivity the console hides behind the screensaver
// and only the signed-in operator can bring it back (re-authenticating at Auth0 — see
// `components/screen-lock/screen-lock.tsx`). This module is the state, with no React:
//
//   - **Activity is shared across tabs.** An operator working in one tab must not have
//     the other tab lock the whole desk, so the last activity time lives in
//     localStorage and every tab measures idle against the newest one.
//   - **The lock is shared across tabs and survives a reload.** Locking writes a record
//     (`{ at, sub }`) to localStorage; the other tabs follow through the `storage`
//     event, and a reload of a locked tab comes back locked. A record for another
//     operator, or older than the session's idle limit, is stale and dropped.
//   - **Activity on the lock screen is not activity.** While locked, input wakes the
//     unlock card but does not count as using the console, so the 30-minute idle
//     sign-out (operator-ui-tds §7) still runs from the last real activity.
//
// It is a privacy screen in front of a live session, not a security boundary on its own:
// the session's tokens stay in memory underneath. Unlocking is what is authenticated.

const LOCK_KEY = 'nexa.operator.screenLock';
const ACTIVITY_KEY = 'nexa.operator.lastActivity';

/** Default minutes of inactivity before the screen locks (`VITE_SCREEN_LOCK_MINUTES`). */
export const DEFAULT_SCREEN_LOCK_MINUTES = 10;
/** The session's idle limit (operator-ui-tds §7): a lock older than this is stale. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

export interface IScreenLockState {
  locked: boolean;
  /** Epoch ms the lock started, `null` when unlocked. */
  lockedAt: number | null;
}

interface IStoredLock {
  at: number;
  sub: string | null;
}

const UNLOCKED: IScreenLockState = { locked: false, lockedAt: null };
let state: IScreenLockState = UNLOCKED;
const listeners = new Set<() => void>();
let currentSub: string | null = null;
let lastLocal = 0;

function emit(next: IScreenLockState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function getScreenLockState(): IScreenLockState {
  return state;
}

export function subscribeScreenLock(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Storage can be missing or throw (private mode, blocked site data). The lock then
// degrades to this tab only — never to an exception in the console.
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // see `read`
  }
}

function parseLock(raw: string | null): IStoredLock | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<IStoredLock>;
    return typeof v.at === 'number' ? { at: v.at, sub: typeof v.sub === 'string' ? v.sub : null } : null;
  } catch {
    return null;
  }
}

/** Last activity in ANY tab of this console (epoch ms). */
export function lastActivityAt(): number {
  const shared = Number(read(ACTIVITY_KEY));
  return Math.max(lastLocal, Number.isFinite(shared) ? shared : 0);
}

/** Lock now (idle timeout, or the operator's own «Bloquear pantalla»). Idempotent. */
export function lockScreen(now: number = Date.now()): void {
  if (state.locked) return;
  write(LOCK_KEY, JSON.stringify({ at: now, sub: currentSub } satisfies IStoredLock));
  emit({ locked: true, lockedAt: now });
}

/** The operator proved who they are: unlock every tab and restart the idle clock. */
export function unlockScreen(now: number = Date.now()): void {
  lastLocal = now;
  write(ACTIVITY_KEY, String(now));
  write(LOCK_KEY, null);
  if (state.locked) emit(UNLOCKED);
}

/**
 * Forget a persisted lock without touching this tab's state — sign-out (the page is
 * leaving anyway; hiding the overlay first would flash the console) and the return
 * from a full-page unlock at Auth0.
 */
export function clearScreenLock(): void {
  write(LOCK_KEY, null);
}

/** `VITE_SCREEN_LOCK_MINUTES` in ms. `0` turns the automatic lock off (manual still works). */
export function screenLockMs(raw: string | undefined = import.meta.env.VITE_SCREEN_LOCK_MINUTES): number {
  const minutes = raw === undefined || raw.trim() === '' ? NaN : Number(raw);
  return (Number.isFinite(minutes) && minutes >= 0 ? minutes : DEFAULT_SCREEN_LOCK_MINUTES) * 60_000;
}

export interface IScreenLockOptions {
  /** Inactivity before locking; `0` = never lock automatically. */
  lockMs: number;
  /** The signed-in operator — a persisted lock only resumes for the same one. */
  sub: string | null;
}

/** Start watching activity for this tab. Returns the teardown. */
export function startScreenLock({ lockMs, sub }: IScreenLockOptions): () => void {
  currentSub = sub;
  const now = Date.now();

  const stored = parseLock(read(LOCK_KEY));
  if (stored && stored.sub === sub && now - stored.at < SESSION_IDLE_MS) {
    if (!state.locked) emit({ locked: true, lockedAt: stored.at });
  } else {
    if (stored) write(LOCK_KEY, null);
    lastLocal = now;
    write(ACTIVITY_KEY, String(now));
  }

  const onActivity = (): void => {
    if (state.locked) return;
    const at = Date.now();
    if (at - lastLocal < 1000) return; // pointermove fires per pixel; once a second is plenty
    lastLocal = at;
    write(ACTIVITY_KEY, String(at));
  };

  // One timeout aimed at the lock deadline, not a ticking interval: when it fires it
  // re-reads the shared activity clock and either locks or re-aims at the new deadline.
  let timer: number | null = null;
  const schedule = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
    if (state.locked || lockMs <= 0) return;
    timer = window.setTimeout(check, Math.max(0, lastActivityAt() + lockMs - Date.now()));
  };
  const check = (): void => {
    if (state.locked || lockMs <= 0) return;
    if (Date.now() - lastActivityAt() >= lockMs) lockScreen();
    else schedule();
  };
  // Unlocked again (here or in another tab): the idle clock starts over.
  const unsubscribe = subscribeScreenLock(() => {
    if (!state.locked) schedule();
  });

  const onStorage = (e: StorageEvent): void => {
    if (e.key !== LOCK_KEY) return;
    const next = parseLock(e.newValue);
    if (next && !state.locked) emit({ locked: true, lockedAt: next.at });
    if (!next && state.locked) {
      lastLocal = Date.now();
      emit(UNLOCKED);
    }
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') check();
  };

  const opts = { capture: true, passive: true } as const;
  for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, opts);
  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisible);
  schedule();

  return () => {
    if (timer !== null) window.clearTimeout(timer);
    unsubscribe();
    for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, opts);
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/** Test seam — module state would otherwise leak between cases. */
export function resetScreenLockForTests(): void {
  state = UNLOCKED;
  listeners.clear();
  currentSub = null;
  lastLocal = 0;
}
